import {useMutation} from '@apollo/react-hooks';

import {CHANNEL_UNKNOWN} from 'nutshell-core/channels/consts';

import type {ChannelType} from 'nutshell-graphql-types';
import type {
    GetSources as GetSourcesQuery,
    GetSourcesVariables as GetSourcesQueryVariables,
    CreateSource as CreateSourceMutation,
    CreateSourceVariables as CreateSourceMutationVariables,
    DeleteSource as DeleteSourceMutation,
    DeleteSourceVariables as DeleteSourceMutationVariables,
    UpdateSource as UpdateSourceMutation,
    UpdateSourceVariables as UpdateSourceMutationVariables,
} from 'nutshell-graphql-types';

import CREATE_SOURCE from './graphql/mutations/create-source.graphql';
import DELETE_SOURCE from './graphql/mutations/delete-source.graphql';
import UPDATE_SOURCE from './graphql/mutations/update-source.graphql';
import GET_SOURCES from './graphql/queries/get-sources.graphql';
import {getQueryVariables} from './helpers';

export function useCreateSource(): (arg1: {
    name: string;
    channel: ChannelType | null | undefined;
}) => Promise<any> {
    const [createSource] = useMutation<CreateSourceMutation, CreateSourceMutationVariables>(
        CREATE_SOURCE
    );

    return (input: {name: string; channel: ChannelType | null | undefined}) => {
        const queryVariables: GetSourcesQueryVariables = getQueryVariables({
            channel: input.channel && input.channel !== CHANNEL_UNKNOWN ? input.channel : null,
        });

        return createSource({
            variables: {
                source: {
                    name: input.name,
                    channel:
                        input.channel && input.channel !== CHANNEL_UNKNOWN
                            ? input.channel
                            : undefined,
                },
            },
            update: (proxy, mutationResults) => {
                const cacheResult: GetSourcesQuery | null | undefined = proxy.readQuery({
                    query: GET_SOURCES,
                    variables: queryVariables,
                });

                const newSource =
                    mutationResults &&
                    mutationResults.data &&
                    mutationResults.data.sourceCreate &&
                    mutationResults.data.sourceCreate.source;

                if (!newSource) return;

                if (cacheResult && cacheResult.sources) {
                    const data = {
                        sources: {
                            ...cacheResult.sources,
                            edges: cacheResult.sources.edges
                                .concat({
                                    node: newSource,
                                    __typename: 'SourcesEdge',
                                })
                                .sort((a, b) => a.node.name.localeCompare(b.node.name)),
                        },
                    } as const;

                    proxy.writeQuery({
                        query: GET_SOURCES,
                        variables: queryVariables,
                        data,
                    });
                }
            },
        });
    };
}

export function useDeleteSource(): (input: {
    id: string;
    channel: ChannelType | null | undefined;
}) => Promise<any> {
    const [deleteSource] = useMutation<DeleteSourceMutation, DeleteSourceMutationVariables>(
        DELETE_SOURCE
    );

    return (input: {id: string; channel: ChannelType | null | undefined}) => {
        const queryVariables: GetSourcesQueryVariables = getQueryVariables({
            channel: input.channel,
        });

        return deleteSource({
            variables: {
                source: {
                    id: input.id,
                },
            },
            optimisticResponse: {
                // @ts-expect-error
                __typename: 'Mutation',
                sourceDelete: {
                    __typename: 'SourceDeletePayload',
                    source: {
                        __typename: 'Source',
                        id: input.id,
                        createdTime: '',
                        name: '',
                        type: 'NONE',
                        isDeleteable: false,
                        isEditable: false,
                        lastUsedTime: null,
                        channel: {
                            __typename: 'Channel',
                            value: 'UNKNOWN',
                            title: '',
                            description: '',
                        },
                    },
                },
            },
            update: (proxy, mutationResults) => {
                const cacheResult: GetSourcesQuery | null | undefined = proxy.readQuery({
                    query: GET_SOURCES,
                    variables: queryVariables,
                });

                const deletedSource =
                    mutationResults &&
                    mutationResults.data &&
                    mutationResults.data.sourceDelete &&
                    mutationResults.data.sourceDelete.source;

                if (!deletedSource) return;

                if (cacheResult && cacheResult.sources) {
                    proxy.writeQuery({
                        query: GET_SOURCES,
                        variables: queryVariables,
                        data: {
                            sources: {
                                ...cacheResult.sources,
                                edges: cacheResult.sources.edges.filter((edge) => {
                                    return edge.node.id !== input.id;
                                }),
                            },
                        },
                    });
                }
            },
        });
    };
}

export function useUpdateSource(): (arg1: {
    id: string;
    name: string;
    oldChannel: ChannelType | null | undefined;
    newChannel: ChannelType | null | undefined;
}) => Promise<any> {
    const [updateSource] = useMutation<UpdateSourceMutation, UpdateSourceMutationVariables>(
        UPDATE_SOURCE
    );

    return (input: {
        id: string;
        name: string;
        oldChannel: ChannelType | null | undefined;
        newChannel: ChannelType | null | undefined;
    }) => {
        const oldQueryVariables: GetSourcesQueryVariables = getQueryVariables({
            channel: input.oldChannel ? input.oldChannel : null,
        });
        const newQueryVariables: GetSourcesQueryVariables = getQueryVariables({
            channel: input.newChannel ? input.newChannel : null,
        });

        return updateSource({
            variables: {
                source: {
                    id: input.id,
                    name: input.name,
                    channel: input.newChannel,
                },
            },
            update: (proxy, mutationResults) => {
                const oldChannelCacheResult: GetSourcesQuery | null | undefined = proxy.readQuery({
                    query: GET_SOURCES,
                    variables: oldQueryVariables,
                });

                const newChannelCacheResult: GetSourcesQuery | null | undefined = proxy.readQuery({
                    query: GET_SOURCES,
                    variables: newQueryVariables,
                });

                const updatedSource =
                    mutationResults &&
                    mutationResults.data &&
                    mutationResults.data.sourceUpdate &&
                    mutationResults.data.sourceUpdate.source;

                if (!updatedSource) return;

                // The old or new channel could be UNKNOWN or null for no channel, for comparison ease we'll just use null
                const newChannel =
                    input.newChannel && input.newChannel !== CHANNEL_UNKNOWN
                        ? input.newChannel
                        : null;
                const oldChannel =
                    input.oldChannel && input.oldChannel !== CHANNEL_UNKNOWN
                        ? input.oldChannel
                        : null;

                if (newChannel !== oldChannel) {
                    if (oldChannelCacheResult && oldChannelCacheResult.sources) {
                        // remove from old channel
                        proxy.writeQuery({
                            query: GET_SOURCES,
                            variables: oldQueryVariables,
                            data: {
                                sources: {
                                    ...oldChannelCacheResult.sources,
                                    edges: oldChannelCacheResult.sources.edges.filter((edge) => {
                                        return edge.node.id !== input.id;
                                    }),
                                },
                            },
                        });
                    }

                    if (newChannelCacheResult && newChannelCacheResult.sources) {
                        // add to new channel
                        proxy.writeQuery({
                            query: GET_SOURCES,
                            variables: newQueryVariables,
                            data: {
                                sources: {
                                    ...newChannelCacheResult.sources,
                                    edges: newChannelCacheResult.sources.edges
                                        .concat({
                                            node: updatedSource,
                                            __typename: 'SourcesEdge',
                                        })
                                        .sort((a, b) => a.node.name.localeCompare(b.node.name)),
                                },
                            },
                        });
                    }
                }
            },
        });
    };
}
